import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api';
import type { Application, Tag } from '../api';
import { useSuperuserSession } from '../auth';
import { Banner, CredentialCallout, Empty, Field, Modal, Spinner, TagEditor, TagList } from '../components/ui';

/** Story 2 & 3: the superadmin sees every application and can create new ones. */
export function Applications() {
  const { token } = useSuperuserSession();
  const [applications, setApplications] = useState<Application[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{
    name: string;
    applicationId: string;
    masterEmail: string;
    delivered: boolean;
    deliveryError?: string;
    temporaryPassword?: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listApplications(token, search);
      setApplications(res.applications);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load applications');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Applications</h1>
          <p className="muted">Each application is an isolated tenant with its own users, roles and permissions.</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          New application
        </button>
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

      {created && (
        <div className="stack">
          <Banner kind="success" onDismiss={() => setCreated(null)}>
            <strong>{created.name}</strong> created. Application ID{' '}
            <code>{created.applicationId}</code>
            {created.delivered && <> — credentials emailed to {created.masterEmail}.</>}
          </Banner>
          {!created.delivered && created.temporaryPassword && (
            <CredentialCallout
              email={created.masterEmail}
              password={created.temporaryPassword}
              reason={created.deliveryError}
            />
          )}
        </div>
      )}

      <input
        className="input input--search"
        placeholder="Search applications…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <Spinner />
      ) : applications.length === 0 ? (
        <Empty>No applications yet. Create one to provision its master account.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Application ID</th>
                <th>Users</th>
                <th>Tags</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.applicationId}>
                  <td>
                    <Link className="link" to={`/admin/applications/${app.applicationId}`}>
                      {app.name}
                    </Link>
                  </td>
                  <td>
                    <code className="small">{app.applicationId}</code>
                  </td>
                  <td>{app.userCount ?? 0}</td>
                  <td>
                    <TagList tags={app.tags} />
                  </td>
                  <td className="muted small">{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td className="right">
                    <Link className="btn btn--small" to={`/admin/applications/${app.applicationId}`}>
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateApplicationModal
          token={token}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreated(result);
            setCreating(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CreateApplicationModal({
  token,
  onClose,
  onCreated,
}: {
  token: string;
  onClose: () => void;
  onCreated: (result: {
    name: string;
    applicationId: string;
    masterEmail: string;
    delivered: boolean;
    deliveryError?: string;
    temporaryPassword?: string;
  }) => void;
}) {
  const [name, setName] = useState('');
  const [masterEmail, setMasterEmail] = useState('');
  const [masterName, setMasterName] = useState('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.createApplication(token, {
        name: name.trim(),
        masterEmail: masterEmail.trim(),
        masterName: masterName.trim() || undefined,
        tags: tags.filter((t) => t.key.trim()),
      });
      onCreated({
        name: res.application.name,
        applicationId: res.application.applicationId,
        masterEmail: res.masterUser.email,
        delivered: res.emailDelivery.delivered,
        deliveryError: res.emailDelivery.error,
        temporaryPassword: res.temporaryPassword,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the application');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="New application"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Create application"
      submitting={submitting}
    >
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Application name">
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Master account email"
        hint="A master account is created and its generated password is emailed to this address."
      >
        <input
          className="input"
          type="email"
          required
          value={masterEmail}
          onChange={(e) => setMasterEmail(e.target.value)}
        />
      </Field>
      <Field label="Master account name" hint="Optional. Defaults to “Master Admin”.">
        <input className="input" value={masterName} onChange={(e) => setMasterName(e.target.value)} />
      </Field>
      <Field label="Tags">
        <TagEditor value={tags} onChange={setTags} />
      </Field>
    </Modal>
  );
}
